<template>
  <div class="admin-logs">
    <div class="page-header">
      <h2>{{ $t('legacy.Системные логи') }}</h2>
      <div class="header-actions">
        <button @click="exportLogs" class="btn-export" :disabled="exporting">
          {{ exporting ? $t('Экспорт...') : $t('Экспорт CSV') }}
        </button>
        <button @click="refreshLogs" class="btn-refresh" :disabled="loading">
          {{ $t('legacy.Обновить') }}
        </button>
      </div>
    </div>

    <div class="stats-panel" v-if="stats">
      <div class="stat-card">
        <div class="stat-value error">{{ stats.recentErrors }}</div>
        <div class="stat-label">{{ $t('legacy.Ошибок за 24ч') }}</div>
      </div>
      <div class="stat-card" v-for="item in stats.bySeverity" :key="item.severity">
        <div :class="['stat-value', item.severity]">{{ item.count }}</div>
        <div class="stat-label">{{ getSeverityLabel(item.severity) }} ({{ $t('7 дней') }})</div>
      </div>
    </div>

    <div class="filters-panel">
      <div class="filter-group">
         <label>{{ $t('legacy.Уровень') }}:</label>
        <select v-model="filters.severity" @change="fetchLogs">
           <option value="all">{{ $t('legacy.Все') }}</option>
           <option value="error">{{ $t('legacy.Ошибки') }}</option>
           <option value="warn">{{ $t('legacy.Предупреждения') }}</option>
           <option value="info">{{ $t('legacy.Информация') }}</option>
           <option value="debug">{{ $t('legacy.Отладка') }}</option>
        </select>
      </div>

      <div class="filter-group">
         <label>{{ $t('legacy.Устройство') }}:</label>
        <select v-model="filters.deviceType" @change="fetchLogs">
           <option value="all">{{ $t('legacy.Все') }}</option>
           <option value="mobile">{{ $t('legacy.Мобильный') }}</option>
           <option value="tablet">{{ $t('legacy.Планшет') }}</option>
           <option value="desktop">{{ $t('ПК') }}</option>
        </select>
      </div>

      <div class="filter-group">
         <label>{{ $t('С даты') }}:</label>
        <input type="date" v-model="filters.startDate" @change="fetchLogs" />
      </div>

      <div class="filter-group">
         <label>{{ $t('По дату') }}:</label>
        <input type="date" v-model="filters.endDate" @change="fetchLogs" />
      </div>

      <div class="filter-group search">
         <label>{{ $t('common.search') }}:</label>
        <input 
          type="text" 
          v-model="filters.search" 
           :placeholder="$t('legacy.Сообщение или компонент...')"
          @input="debouncedSearch"
        />
      </div>

       <button @click="clearFilters" class="btn-clear">{{ $t('legacy.Сбросить') }}</button>
    </div>

    <div class="logs-table" v-if="!loading">
      <table>
        <thead>
          <tr>
            <th style="width: 50px;"></th>
             <th style="width: 140px;">{{ $t('legacy.Время') }}</th>
             <th style="width: 80px;">{{ $t('legacy.Уровень') }}</th>
             <th style="width: 100px;">{{ $t('legacy.Устройство') }}</th>
             <th style="width: 120px;">{{ $t('legacy.Пользователь') }}</th>
             <th>{{ $t('legacy.Сообщение') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="log in logs" :key="log.id">
            <tr :class="['log-row', log.severity]" @click="toggleExpand(log.id)">
              <td class="expand-cell">
                <span :class="['expand-icon', { expanded: expandedLogs.includes(log.id) }]">▶</span>
              </td>
              <td>{{ formatDateTime(log.created_at) }}</td>
              <td>
                <span :class="['severity-badge', log.severity]">
                  {{ getSeverityLabel(log.severity) }}
                </span>
              </td>
              <td>
                <span class="device-info">
                  {{ getDeviceIcon(log.device_info) }} {{ getDeviceLabel(log.device_info) }}
                </span>
              </td>
               <td>{{ log.user_name || log.user_email || $t('legacy.Гость') }}</td>
              <td class="message-cell">{{ truncate(log.message, 80) }}</td>
            </tr>
            <tr v-if="expandedLogs.includes(log.id)" class="details-row">
              <td colspan="6">
                <div class="log-details">
                  <div class="detail-section">
                     <h4>{{ $t('legacy.Полное сообщение') }}</h4>
                    <pre class="message-full">{{ log.message }}</pre>
                  </div>
                  <div class="detail-section" v-if="log.component === 'ProdamusCheckout' && log.context">
                    <h4>{{ $t('admin.paymentDiagnostic') }}</h4>
                    <p><strong>{{ $t('admin.paymentRequestId') }}:</strong> {{ log.context.requestId }}</p>
                    <p><strong>{{ $t('admin.paymentOrderId') }}:</strong> {{ log.context.orderId || '—' }}</p>
                    <p><strong>{{ $t('legacy.Время') }}:</strong> {{ log.context.occurredAt }}</p>
                    <p><strong>{{ $t('admin.paymentStage') }}:</strong> {{ log.context.stage }}</p>
                    <p><strong>{{ $t('admin.paymentReason') }}:</strong> {{ log.context.explanation }} ({{ log.context.reason }})</p>
                    <p v-if="log.context.httpStatus"><strong>HTTP Prodamus:</strong> {{ log.context.httpStatus }}</p>
                    <p v-if="log.context.networkCode"><strong>{{ $t('admin.paymentNetworkCode') }}:</strong> {{ log.context.networkCode }}</p>
                     <p v-if="log.context.sqlState"><strong>SQLSTATE:</strong> {{ log.context.sqlState }}</p>
                     <p v-if="log.context.databaseCategory"><strong>{{ $t('admin.paymentDatabaseCategory') }}:</strong> {{ log.context.databaseCategory }}</p>
                    <p v-if="log.context.amount !== undefined"><strong>{{ $t('legacy.Сумма к оплате:') }}</strong> {{ log.context.amount }} {{ log.context.currency }}</p>
                    <p><strong>{{ $t('admin.paymentElapsed') }}:</strong> {{ log.context.elapsedMs }}</p>
                  </div>

                  <div class="detail-section" v-if="log.stack_trace">
                    <h4>Stack Trace</h4>
                    <pre class="stack-trace">{{ log.stack_trace }}</pre>
                  </div>
                  
                  <div class="detail-section">
                     <h4>{{ $t('legacy.Информация об устройстве') }}</h4>
                    <div class="device-details">
                      <div v-if="log.device_info">
                         <p><strong>{{ $t('legacy.Тип') }}:</strong> {{ log.device_info.deviceType }}</p>
                         <p><strong>{{ $t('legacy.ОС') }}:</strong> {{ log.device_info.os }}</p>
                         <p><strong>{{ $t('legacy.Браузер') }}:</strong> {{ log.device_info.browser }}</p>
                         <p><strong>{{ $t('Экран') }}:</strong> {{ log.device_info.screenWidth }}x{{ log.device_info.screenHeight }}</p>
                         <p><strong>Viewport:</strong> {{ log.device_info.viewportWidth }}x{{ log.device_info.viewportHeight }}</p>
                         <p><strong>Touch:</strong> {{ log.device_info.touchSupport ? $t('common.yes') : $t('common.no') }}</p>
                        <p><strong>User Agent:</strong></p>
                        <pre class="user-agent">{{ log.device_info.userAgent }}</pre>
                      </div>
                    </div>
                  </div>

                  <div class="detail-section" v-if="log.context">
                     <h4>{{ $t('legacy.Контекст') }}</h4>
                    <pre class="context-json">{{ JSON.stringify(log.context, null, 2) }}</pre>
                  </div>

                  <div class="detail-section">
                     <h4>{{ $t('legacy.Дополнительно') }}</h4>
                    <p><strong>URL:</strong> {{ log.page_url }}</p>
                     <p><strong>{{ $t('Компонент') }}:</strong> {{ log.component || '-' }}</p>
                     <p><strong>{{ $t('ID пользователя') }}:</strong> {{ log.user_id || '-' }}</p>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div class="empty-state" v-if="logs.length === 0">
         {{ $t('legacy.Логи не найдены') }}
      </div>
    </div>

    <div class="loading" v-else>
       {{ $t('common.loading') }}
    </div>

    <div class="pagination" v-if="pagination.totalPages > 1">
      <button 
        @click="goToPage(pagination.page - 1)" 
        :disabled="pagination.page <= 1"
      >
         {{ $t('common.back') }}
      </button>
       <span>{{ $t('legacy.Страница') }} {{ pagination.page }} {{ $t('legacy.из') }} {{ pagination.totalPages }}</span>
      <button 
        @click="goToPage(pagination.page + 1)" 
        :disabled="pagination.page >= pagination.totalPages"
      >
         {{ $t('Вперёд') }}
      </button>
    </div>

    <div class="cleanup-section">
       <h3>{{ $t('legacy.Очистка логов') }}</h3>
       <p>{{ $t('legacy.Удалить логи старше:') }}</p>
      <div class="cleanup-actions">
         <button @click="cleanupLogs(7)" class="btn-cleanup">7 {{ $t('дней') }}</button>
         <button @click="cleanupLogs(30)" class="btn-cleanup">30 {{ $t('дней') }}</button>
         <button @click="cleanupLogs(90)" class="btn-cleanup">90 {{ $t('дней') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/api/axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminLogs',
  data() {
    return {
      toast: null,
      logs: [],
      stats: null,
      loading: false,
      exporting: false,
      expandedLogs: [],
      filters: {
        severity: 'all',
        deviceType: 'all',
        startDate: '',
        endDate: '',
        search: ''
      },
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
      },
      searchTimeout: null
    };
  },
  created() {
    this.toast = useToast();
  },
  mounted() {
    this.fetchLogs();
    this.fetchStats();
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          page: this.pagination.page,
          limit: this.pagination.limit
        });

        if (this.filters.severity !== 'all') params.append('severity', this.filters.severity);
        if (this.filters.deviceType !== 'all') params.append('deviceType', this.filters.deviceType);
        if (this.filters.startDate) params.append('startDate', this.filters.startDate);
        if (this.filters.endDate) params.append('endDate', this.filters.endDate);
        if (this.filters.search) params.append('search', this.filters.search);

        const response = await axios.get(`/logs/admin?${params}`);

        if (response.data.success) {
          this.logs = response.data.logs;
          this.pagination = response.data.pagination;
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
         this.toast.error(this.$t('Ошибка загрузки логов'));
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const response = await axios.get('/logs/admin/stats');

        if (response.data.success) {
          this.stats = response.data.stats;
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async exportLogs() {
      this.exporting = true;
      try {
        const params = new URLSearchParams();
        
        if (this.filters.severity !== 'all') params.append('severity', this.filters.severity);
        if (this.filters.startDate) params.append('startDate', this.filters.startDate);
        if (this.filters.endDate) params.append('endDate', this.filters.endDate);

        const response = await axios.get(`/logs/admin/export?${params}`);

        const csvContent = this.convertToCSV(response.data.logs || response.data);
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
         this.toast.success(this.$t('Логи экспортированы'));
      } catch (error) {
        console.error('Error exporting logs:', error);
         this.toast.error(this.$t('legacy.Ошибка экспорта'));
      } finally {
        this.exporting = false;
      }
    },
    
    convertToCSV(logs) {
      if (!logs || logs.length === 0) return '';
      
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };
      
      const headers = [
        'ID',
        this.$t('legacy.Дата'),
        this.$t('legacy.Уровень'),
        this.$t('legacy.Сообщение'),
        this.$t('legacy.Пользователь'),
        this.$t('legacy.Устройство'),
        this.$t('legacy.Браузер'),
        this.$t('legacy.ОС'),
        'Stack Trace',
        this.$t('legacy.Контекст')
      ];
      const rows = logs.map(log => {
        const deviceInfo = log.device_info || {};
        return [
          log.id,
          this.$formatDate(new Date(log.created_at), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }),
          log.severity,
          log.message,
          log.user_id || '',
          deviceInfo.deviceType || '',
          deviceInfo.browser || '',
          deviceInfo.os || '',
          log.stack_trace || '',
          JSON.stringify(log.context || {})
        ].map(escapeCSV).join(',');
      });
      
      return headers.join(',') + '\n' + rows.join('\n');
    },

    async cleanupLogs(days) {
       if (!confirm(this.$t('Удалить все логи старше {days} дней?', { days }))) return;
      
      try {
        const response = await axios.delete(`/logs/admin/cleanup?days=${days}`);

        if (response.data.success) {
          this.toast.success(response.data.message);
          this.fetchLogs();
          this.fetchStats();
        }
      } catch (error) {
        console.error('Error cleaning up logs:', error);
         this.toast.error(this.$t('Ошибка очистки логов'));
      }
    },

    refreshLogs() {
      this.fetchLogs();
      this.fetchStats();
    },

    clearFilters() {
      this.filters = {
        severity: 'all',
        deviceType: 'all',
        startDate: '',
        endDate: '',
        search: ''
      };
      this.pagination.page = 1;
      this.fetchLogs();
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchLogs();
      }, 500);
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.page = page;
      this.fetchLogs();
    },

    toggleExpand(logId) {
      const index = this.expandedLogs.indexOf(logId);
      if (index > -1) {
        this.expandedLogs.splice(index, 1);
      } else {
        this.expandedLogs.push(logId);
      }
    },

    getSeverityLabel(severity) {
      const labels = {
         error: this.$t('legacy.Ошибка'),
         warn: this.$t('legacy.Внимание'),
         info: this.$t('Инфо'),
         debug: this.$t('legacy.Отладка')
      };
      return labels[severity] || severity;
    },

    getDeviceIcon(deviceInfo) {
      if (!deviceInfo) return '❓';
      const icons = {
        mobile: '📱',
        tablet: '📱',
        desktop: '💻'
      };
      return icons[deviceInfo.deviceType] || '❓';
    },

    getDeviceLabel(deviceInfo) {
       if (deviceInfo?.deviceType === 'server') return 'Server';
       if (!deviceInfo) return this.$t('legacy.Неизвестно');
       return `${deviceInfo.os || ''} ${deviceInfo.browser || ''}`.trim() || this.$t('legacy.Неизвестно');
    },

    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return this.$formatDate(d, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    truncate(str, length) {
      if (!str) return '';
      return str.length > length ? str.substring(0, length) + '...' : str;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/global.scss";

.admin-logs {
  padding: 20px;
  max-width: 1400px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    color: #333;
  }

  .header-actions {
    display: flex;
    gap: 10px;
  }
}

.stats-panel {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .stat-card {
    background: white;
    border-radius: 8px;
    padding: 15px 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    min-width: 120px;
    text-align: center;

    .stat-value {
      font-size: 24px;
      font-weight: bold;
      
      &.error { color: #dc3545; }
      &.warn { color: #ffc107; }
      &.info { color: #17a2b8; }
      &.debug { color: #6c757d; }
    }

    .stat-label {
      font-size: 12px;
      color: #666;
      margin-top: 5px;
    }
  }
}

.filters-panel {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: flex-end;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 5px;

    label {
      font-size: 12px;
      color: #666;
    }

    select, input {
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
    }

    &.search {
      flex: 1;
      min-width: 200px;
    }
  }
}

.logs-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th, td {
    padding: 12px 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    font-size: 13px;
    color: #555;
  }

  .log-row {
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #f8f9fa;
    }

    &.error {
      border-left: 3px solid #dc3545;
    }
    &.warn {
      border-left: 3px solid #ffc107;
    }
    &.info {
      border-left: 3px solid #17a2b8;
    }
    &.debug {
      border-left: 3px solid #6c757d;
    }
  }

  .expand-cell {
    text-align: center;
  }

  .expand-icon {
    display: inline-block;
    transition: transform 0.2s;
    color: #999;

    &.expanded {
      transform: rotate(90deg);
    }
  }

  .severity-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;

    &.error { background: #fee; color: #c00; }
    &.warn { background: #fff3cd; color: #856404; }
    &.info { background: #d1ecf1; color: #0c5460; }
    &.debug { background: #e9ecef; color: #495057; }
  }

  .device-info {
    font-size: 12px;
    white-space: nowrap;
  }

  .message-cell {
    font-family: monospace;
    font-size: 12px;
    color: #333;
  }

  .details-row {
    background: #f8f9fa;

    td {
      padding: 0;
    }
  }

  .log-details {
    padding: 20px;

    .detail-section {
      margin-bottom: 20px;

      h4 {
        margin: 0 0 10px;
        font-size: 14px;
        color: #333;
      }

      pre {
        background: #2d2d2d;
        color: #f8f8f2;
        padding: 15px;
        border-radius: 4px;
        overflow-x: auto;
        font-size: 12px;
        margin: 0;
        white-space: pre-wrap;
        word-break: break-all;
      }

      .user-agent {
        font-size: 11px;
      }
    }

    .device-details {
      background: #fff;
      padding: 15px;
      border-radius: 4px;
      border: 1px solid #eee;

      p {
        margin: 5px 0;
        font-size: 13px;
      }
    }
  }
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #666;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  span {
    color: #666;
  }
}

.cleanup-section {
  margin-top: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  h3 {
    margin: 0 0 10px;
    font-size: 16px;
  }

  p {
    margin: 0 0 15px;
    color: #666;
    font-size: 14px;
  }

  .cleanup-actions {
    display: flex;
    gap: 10px;
  }
}

.btn-export, .btn-refresh {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-export {
  background: #28a745;
  color: white;

  &:disabled {
    background: #ccc;
  }
}

.btn-refresh {
  background: #007bff;
  color: white;

  &:disabled {
    background: #ccc;
  }
}

.btn-clear {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-cleanup {
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #c82333;
  }
}
</style>
